import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { UploadCloud, Trash2, Building2 } from "lucide-react";

// A small hand-curated list. Users can also type any IANA zone.
const COMMON_TIMEZONES = [
  "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane",
  "Australia/Perth", "Australia/Adelaide", "Australia/Darwin",
  "Australia/Hobart", "Pacific/Auckland",
  "Asia/Kolkata", "Asia/Singapore", "Asia/Dubai",
  "Europe/London", "Europe/Berlin",
  "America/New_York", "America/Los_Angeles", "UTC",
];

export default function OrgSettings() {
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [tz, setTz] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const fileInput = useRef(null);

  const load = () => api.get("/org/settings").then((r) => {
    setOrg(r.data);
    setName(r.data.name || "");
    setTagline(r.data.tagline || "");
    setTz(r.data.timezone || "Australia/Sydney");
    setLogoBust(Date.now());
  });

  useEffect(() => { load(); }, []);

  async function saveDetails() {
    setSaving(true);
    try {
      await api.put("/org/settings", { name, tagline, timezone: tz });
      toast.success("Organisation details saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function uploadLogo(file) {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post("/org/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded");
      setLogoBust(Date.now());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function removeLogo() {
    if (!window.confirm("Remove the current logo?")) return;
    try {
      await api.delete("/org/logo");
      toast.success("Logo removed");
      setLogoBust(Date.now());
      await load();
    } catch {
      toast.error("Remove failed");
    }
  }

  if (!org) return <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>;

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const logoSrc = org.logo_ext ? `${backendBase}/api/org/logo?v=${logoBust}` : null;

  return (
    <div className="space-y-6" data-testid="org-settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Super admin</p>
        <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]">Organisation settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Brand your workspace. These values appear in the header, the login page, and every generated document.
        </p>
      </div>

      {/* Details */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-[#1A7F6E]" />
            <h2 className="text-lg font-semibold text-[#0D1B2A]">Details</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Organisation name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="org-name-input"
                placeholder="Next Provider Australia"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Tagline</Label>
              <Input
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                data-testid="org-tagline-input"
                placeholder="NDIS registration consulting"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Timezone</Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger data-testid="org-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMMON_TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">
                Used for stamping activity, login sessions and email digests.
              </p>
            </div>
          </div>
          <div className="pt-3">
            <Button
              onClick={saveDetails}
              disabled={saving}
              className="bg-[#1A7F6E] hover:bg-[#136053] text-white"
              data-testid="save-org-settings-button"
            >
              {saving ? "Saving…" : "Save details"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logo */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-[#0D1B2A]">Logo</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                PNG · JPG · SVG · max 500 KB · aim for <strong>300×200 px</strong>
              </p>
            </div>
            {org.logo_ext && (
              <Button
                variant="outline"
                onClick={removeLogo}
                className="text-rose-600 border-rose-200 hover:bg-rose-50"
                data-testid="delete-logo-button"
              >
                <Trash2 className="w-4 h-4 mr-1.5" />Remove
              </Button>
            )}
          </div>

          <div className="grid md:grid-cols-[300px_1fr] gap-6 items-start">
            <div
              className="border-2 border-dashed border-slate-200 rounded-lg h-[200px] w-[300px] flex items-center justify-center bg-slate-50"
              data-testid="org-logo-preview"
            >
              {logoSrc ? (
                <img src={logoSrc} alt="Organisation logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <p className="text-sm text-slate-400">No logo uploaded</p>
              )}
            </div>
            <div className="space-y-3">
              <label
                htmlFor="logo-upload"
                className="inline-flex items-center gap-2 bg-[#0D1B2A] hover:bg-[#1a2a3d] text-white text-sm px-4 py-2 rounded-md cursor-pointer"
                data-testid="logo-upload-label"
              >
                <UploadCloud className="w-4 h-4" />
                {uploading ? "Uploading…" : org.logo_ext ? "Replace logo" : "Upload logo"}
              </label>
              <input
                ref={fileInput}
                id="logo-upload"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,.svg,.png,.jpg,.jpeg"
                className="hidden"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="logo-upload-input"
              />
              <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1">
                <li>PNG or JPG at exactly <strong>300 × 200 px</strong> (±40 px tolerance)</li>
                <li>SVG accepted at any size — it will scale automatically</li>
                <li>File size limit: 500 KB</li>
                <li>Transparent background recommended for the header</li>
              </ul>
              {org.logo_updated_at && (
                <p className="text-xs text-slate-400">
                  Last updated {new Date(org.logo_updated_at).toLocaleString()} · Format: {org.logo_ext?.toUpperCase()}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
